// GDPR Consent Management — 3 categories + server-side audit log
import { supabase } from '@/integrations/supabase/client';

export const BANNER_VERSION = 'v2.3';

const STORAGE_KEYS = {
  CONSENT: 'gdpr_consent',
  CONSENT_DATE: 'gdpr_consent_date',
  TRACKING_CONSENT: 'gdpr_tracking_consent',
  MARKETING_CONSENT: 'gdpr_marketing_consent',
  BANNER_SHOWN: 'gdpr_banner_shown',
  BANNER_VERSION: 'gdpr_banner_version',
  ANON_ID: 'gdpr_anonymous_id',
};

export interface GDPRConsent {
  consent: boolean;
  consentDate: Date | null;
  trackingConsent: boolean;
  marketingConsent: boolean;
  bannerVersion: string | null;
}

const getOrCreateAnonId = (): string => {
  if (typeof localStorage === 'undefined') return '';
  let id = localStorage.getItem(STORAGE_KEYS.ANON_ID);
  if (!id) {
    id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(STORAGE_KEYS.ANON_ID, id);
  }
  return id;
};

const logConsentToServer = async (
  essential: boolean,
  analytics: boolean,
  marketing: boolean,
  action: 'accept' | 'reject' | 'customize' | 'update',
) => {
  try {
    await supabase.functions.invoke('log-consent', {
      body: {
        anonymous_id: getOrCreateAnonId(),
        banner_version: BANNER_VERSION,
        essential,
        analytics,
        marketing,
        source: typeof window !== 'undefined' ? window.location.hostname : 'roautomarket.ro',
        action,
      },
    });
  } catch (e) {
    console.warn('[gdpr] failed to log consent', e);
  }
};

export const saveGDPRConsent = (
  consent: boolean,
  trackingConsent: boolean,
  marketingConsent: boolean = false,
): void => {
  if (typeof localStorage === 'undefined') return;

  localStorage.setItem(STORAGE_KEYS.CONSENT, consent.toString());
  localStorage.setItem(STORAGE_KEYS.CONSENT_DATE, new Date().toISOString());
  localStorage.setItem(STORAGE_KEYS.TRACKING_CONSENT, trackingConsent.toString());
  localStorage.setItem(STORAGE_KEYS.MARKETING_CONSENT, marketingConsent.toString());
  localStorage.setItem(STORAGE_KEYS.BANNER_SHOWN, 'true');
  localStorage.setItem(STORAGE_KEYS.BANNER_VERSION, BANNER_VERSION);

  const action: 'accept' | 'reject' | 'customize' =
    trackingConsent && marketingConsent ? 'accept'
    : !trackingConsent && !marketingConsent ? 'reject'
    : 'customize';

  // Fire-and-forget server log
  void logConsentToServer(true, trackingConsent, marketingConsent, action);

  // Notify index.html loader to inject the matching scripts now
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('gdpr-consent-updated'));
  }
};

export const getGDPRConsent = (): GDPRConsent => {
  if (typeof localStorage === 'undefined') {
    return { consent: false, consentDate: null, trackingConsent: false, marketingConsent: false, bannerVersion: null };
  }
  const consent = localStorage.getItem(STORAGE_KEYS.CONSENT);
  const consentDate = localStorage.getItem(STORAGE_KEYS.CONSENT_DATE);
  return {
    consent: consent === 'true',
    consentDate: consentDate ? new Date(consentDate) : null,
    trackingConsent: localStorage.getItem(STORAGE_KEYS.TRACKING_CONSENT) === 'true',
    marketingConsent: localStorage.getItem(STORAGE_KEYS.MARKETING_CONSENT) === 'true',
    bannerVersion: localStorage.getItem(STORAGE_KEYS.BANNER_VERSION),
  };
};

export const hasGivenConsent = (): boolean => {
  if (typeof localStorage === 'undefined') return false;
  const hasRecord = localStorage.getItem(STORAGE_KEYS.CONSENT) !== null;
  if (!hasRecord) return false;
  // Re-prompt if banner version changed
  const storedVersion = localStorage.getItem(STORAGE_KEYS.BANNER_VERSION);
  return storedVersion === BANNER_VERSION;
};

export const shouldShowGDPRBanner = (): boolean => {
  if (typeof localStorage === 'undefined') return false;
  return !hasGivenConsent();
};

export const clearGDPRConsent = (): void => {
  if (typeof localStorage === 'undefined') return;
  // Preserve the anonymous_id so we can correlate consent history
  const keep = localStorage.getItem(STORAGE_KEYS.ANON_ID);
  Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  if (keep) localStorage.setItem(STORAGE_KEYS.ANON_ID, keep);
};

// Event-based system for GDPR banner visibility
const GDPR_BANNER_EVENT = 'gdpr-banner-visibility';

export interface GDPRBannerEvent {
  visible: boolean;
}

export const subscribeToGDPRBanner = (listener: (visible: boolean) => void): (() => void) => {
  const handler = (event: CustomEvent<GDPRBannerEvent>) => listener(event.detail.visible);
  window.addEventListener(GDPR_BANNER_EVENT, handler as EventListener);
  return () => window.removeEventListener(GDPR_BANNER_EVENT, handler as EventListener);
};

export const openCookieSettings = (): void => {
  clearGDPRConsent();
  const event = new CustomEvent<GDPRBannerEvent>(GDPR_BANNER_EVENT, { detail: { visible: true } });
  window.dispatchEvent(event);
};
